<template>
  <div id="app" class="app">
    <transition name="fade">
      <div id="page" class="page" v-if="pageLoaded">
        <transition name="fade">
          <Menu
            @setAmount="setAmount"
            v-if="menuVisible"
            @scrollToSection="scrollToSection"
            :currency="currency"
            :currencyValue="currencyValue"
            :currencyCode="currencyCode"
            @changeCurrency="changeCurrency"
            @openSellSkins="handleSellSkinsClick"
          />
        </transition>
        <Header
          @setAmount="setAmount"
          @scrollToSection="scrollToSection"
          :currency="currency"
          :currencyValue="currencyValue"
          :currencyCode="currencyCode"
          @changeCurrency="changeCurrency"
          @openSellSkins="handleSellSkinsClick"
        />
        <router-view
          @setAmount="setAmount"
          @topUp="topUp"
          @formatDate="formatDate"
          :currency="currency"
          :currencyCode="currencyCode"
          @openProductModal="openProductModal"
        />
        <Footer
          :server-ip="ip"
          :server-port="port"
          :payment-methods="settings.payment_methods || []"
          @goToPage="goToPage"
        />
        <transition name="fade">
          <ProductModal
            v-if="productModalIsVisible"
            @closeProductModal="closeProductModal"
            :currency="currency"
          />
        </transition>
        <transition name="fade">
          <Login
            v-if="signInModalIsVisible"
            @closeSignInModal="closeSignInModal"
            @login2="login2"
          />
        </transition>
        <transition name="fade">
          <TopUpModal
            v-if="topUpModalIsVisible"
            :currency="currency"
            :currencyValue="currencyValue"
            :currencyCode="currencyCode"
            @setTopUpAmount="setTopUpAmount"
          />
        </transition>
        <transition name="fade">
          <OrderModal
            v-if="orderModalIsVisible"
            :currency="currency"
            :payment-methods="settings.payment_methods || []"
            @topUp="topUp"
          />
        </transition>
        <transition name="fade">
          <AgeModal v-if="ageModalIsVisible" />
        </transition>
        <transition name="fade">
          <ErrorModal v-if="errorModalIsVisible" />
        </transition>
        <transition name="fade">
          <SellModal v-if="sellModalIsVisible" />
        </transition>
        <transition name="fade">
          <ComingSoonModal
            v-if="comingSoonModalIsVisible"
            @close="closeComingSoonModal"
          />
        </transition>
        <transition name="fade">
          <WithdrawModal
            v-if="withdrawModalIsVisible && settings.payout_enabled"
            :currency="currency"
          />
        </transition>
        <transition name="fade">
          <CounterOfferModal v-if="counterOfferModalIsVisible" />
        </transition>
        <div v-if="cookesModalIsVisible" class="modal-overlay">
          <div class="cookies-modal">
            <div class="title">{{ $t(`Cookies Unwrapped`) }}</div>
            <div class="desc">
              We use cookies to enhance your experience on our website.
              Essential cookies are necessary for basic operations and are set
              by default. We also employ additional cookies for various purposes
              that enhance your experience but are not required for core
              functions. You can adjust your cookie settings at any time. For
              more details, please refer to our Cookie Notice.
              <a class="desc" @click="goToPage('cookie')">{{
                $t(`Cookie Notice`)
              }}</a
              >.
            </div>
            <button class="button" @click="acceptCookies">
              {{ $t(`Ok, got it!`) }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Login from './components/Login.vue'

import i18n from '@/plugins/i18n'
import { loadScriptsFromString } from '@/utils/load_scripts'
import { scroller } from 'vue-scrollto/src/scrollTo'
import AgeModal from './components/AgeModal.vue'
import ComingSoonModal from './components/ComingSoonModal.vue'
import CounterOfferModal from './components/CounterOfferModal.vue'
import ErrorModal from './components/ErrorModal.vue'
import Footer from './components/Footer.vue'
import Header from './components/Header.vue'
import Menu from './components/Menu.vue'
import OrderModal from './components/OrderModal.vue'
import ProductModal from './components/ProductModal.vue'
import SellModal from './components/SellModal.vue'
import TopUpModal from './components/TopUpModal.vue'
import WithdrawModal from './components/WithdrawModal.vue'

export default {
  name: 'app',
  components: {
    Header,
    Menu,
    Footer,
    ProductModal,
    Login,
    TopUpModal,
    OrderModal,
    AgeModal,
    ErrorModal,
    SellModal,
    WithdrawModal,
    CounterOfferModal,
    ComingSoonModal,
  },
  data: function () {
    return {
      languages: [],
      selectedLanguageId: null,
      ageModalIsVisible: true,
      comingSoonModalIsVisible: false,
      pageLoaded: false,
      menuVisible: false,
      isAuth: false,
      error: '',
      successMessage: '',
      orderHistory: [],
      depositHistory: [],
      textPageTitle: '',
      textPageHeader: '',
      textPageContent: '',
      imgDomain: '',
      textPageList: '',
      currency: '',
      currencyValue: '',
      currencyCode: '',
      currencyOptions: [],
      hostname: '',
      productModalIsVisible: false,
      chosenProoduct: {},
      profileData: {},
      countryOptions: [],
      amount: null,
      analyticsInitialized: false,
      settings: {},
      signInModalIsVisible: false,
      topUpModalIsVisible: false,
      orderModalIsVisible: false,
      topUpAmount: '',
      ip: '',
      port: '',
      cookesModalIsVisible: false,
      errorModalIsVisible: false,
      errorModalMessage: '',
      sellModalIsVisible: false,
      withdrawModalIsVisible: false,
      inventoryItems: [],
      sellRequestHistory: [],
      sellRequestMeta: { current_page: 1, last_page: 1, total: 0 },
      counterOfferModalIsVisible: false,
      counterOfferRequest: null,
      counterOfferError: '',
      sellRequestActionLoading: false,
    }
  },
  watch: {
    $route() {
      window.scrollTo(0, 0)
      this.closeMenu()
      if (this.isAuth) {
        this.getProfileData()
      }
    },
  },
  methods: {
    handleSellSkinsClick() {
      this.closeMenu()
      this.openSellSkins()
    },

    openComingSoonModal() {
      this.comingSoonModalIsVisible = true
    },

    closeComingSoonModal() {
      this.comingSoonModalIsVisible = false
    },

    openSellSkins() {
      const payoutsEnabled =
        this.settings && this.settings.payout_enabled === true

      const payoutMethods = this.settings && this.settings.payout_methods

      const methods = Array.isArray(payoutMethods)
        ? payoutMethods
        : payoutMethods && typeof payoutMethods === 'object'
          ? Object.values(payoutMethods)
          : []

      const hasActivePayoutMethod = methods.some(
        (method) => method === 'card' || method === 'sepa',
      )

      if (!payoutsEnabled || !hasActivePayoutMethod) {
        this.openComingSoonModal()
        return
      }

      if (!this.isAuth) {
        this.openSignInModal()
        return
      }

      this.openSellItemsModal()
    },
    closeAgeModal() {
      this.ageModalIsVisible = false
    },
    cookiesModal() {
      const cookiesAccepted = localStorage.getItem('cookiesAccepted')

      if (!cookiesAccepted) {
        this.cookesModalIsVisible = true
      }
    },
    acceptCookies() {
      localStorage.setItem('cookiesAccepted', 'true')
      this.cookesModalIsVisible = false
    },
    setTopUpAmount(amount) {
      this.topUpAmount = amount
      this.openOrderModal()
    },
    closeOrderModal() {
      this.orderModalIsVisible = false
      this.error = ''
    },
    openOrderModal() {
      this.topUpModalIsVisible = false
      this.orderModalIsVisible = true
    },
    closeTopUpModal() {
      this.topUpModalIsVisible = false
      this.error = ''
    },
    openTopUpModal() {
      this.orderModalIsVisible = false
      this.topUpModalIsVisible = true
    },
    openWithdrawModal() {
      this.withdrawModalIsVisible = true
    },
    closeWithdrawModal() {
      this.withdrawModalIsVisible = false
      this.getProfileData()
    },
    openSellItemsModal() {
      this.$http
        .get(process.env.VUE_APP_API + 'user/inventory')
        .then((res) => {
          if (res.data.status === 'OK') {
            this.openSellModal(res.data.payload)
          } else {
            this.openErrorModal('Failed to load inventory')
          }
        })
        .catch((err) => {
          if (err.response) {
            if (err.response.status === 404) {
              this.openErrorModal(
                'Please join the game server first to sell items',
              )
            } else if (err.response.status === 503) {
              this.openErrorModal(
                'Game server is currently unavailable. Please try again later',
              )
            } else if (err.response.status === 401) {
              this.openSignInModal()
            } else {
              this.openErrorModal('Failed to load inventory')
            }
          } else {
            this.openErrorModal('Failed to load inventory')
          }
        })
    },
    openErrorModal(message) {
      this.errorModalMessage = message
      this.errorModalIsVisible = true
    },
    closeErrorModal() {
      this.errorModalIsVisible = false
      this.errorModalMessage = ''
    },
    openSellModal(items) {
      this.inventoryItems = items.map((item) => ({
        ...item,
        _quantity: 0,
        _price: item.catalog_item ? item.catalog_item.price : 0,
      }))
      this.sellModalIsVisible = true
    },
    closeSellModal() {
      this.sellModalIsVisible = false
      this.inventoryItems = []
      this.getSellRequestHistory()
    },
    getSellRequestHistory(page) {
      let url = process.env.VUE_APP_API + 'user/sell-requests'
      if (page) {
        url += '?page=' + page
      }
      this.$http
        .get(url)
        .then((res) => {
          this.sellRequestHistory = res.data.payload
          if (res.data.meta) {
            this.sellRequestMeta = res.data.meta
          }
        })
        .catch(() => {})
    },
    openCounterOfferModal(req) {
      this.counterOfferRequest = req
      this.counterOfferError = ''
      this.counterOfferModalIsVisible = true
    },
    closeCounterOfferModal() {
      this.counterOfferModalIsVisible = false
      this.counterOfferRequest = null
      this.counterOfferError = ''
    },
    approveCounterOffer() {
      if (!this.counterOfferRequest || this.sellRequestActionLoading) return
      this.sellRequestActionLoading = true
      this.counterOfferError = ''
      this.$http
        .post(
          process.env.VUE_APP_API +
            'user/sell-requests/' +
            this.counterOfferRequest.id +
            '/approve',
        )
        .then((res) => {
          if (res.data.status === 'OK') {
            this.closeCounterOfferModal()
            this.getSellRequestHistory(this.sellRequestMeta.current_page)
            this.getProfileData()
          }
        })
        .catch((err) => {
          if (err.response) {
            if (err.response.status === 401) {
              this.closeCounterOfferModal()
              this.openSignInModal()
            } else if (err.response.status === 503) {
              this.counterOfferError =
                'Game server is currently unavailable. Please try again later.'
            } else if (err.response.status === 409) {
              this.counterOfferError = 'This offer is no longer available.'
            } else if (err.response.data && err.response.data.message) {
              this.counterOfferError = err.response.data.message
            } else {
              this.counterOfferError = 'Something went wrong. Please try again.'
            }
          } else {
            this.counterOfferError =
              'Network error. Please check your connection.'
          }
        })
        .finally(() => {
          this.sellRequestActionLoading = false
        })
    },
    declineCounterOffer(id) {
      if (this.sellRequestActionLoading) return
      this.sellRequestActionLoading = true
      this.$http
        .post(process.env.VUE_APP_API + 'user/sell-requests/' + id + '/decline')
        .then((res) => {
          if (res.data.status === 'OK') {
            this.closeCounterOfferModal()
            this.getSellRequestHistory(this.sellRequestMeta.current_page)
          }
        })
        .catch((err) => {
          if (err.response) {
            if (err.response.status === 401) {
              this.openSignInModal()
            } else if (err.response.data && err.response.data.message) {
              this.openErrorModal(err.response.data.message)
            } else {
              this.openErrorModal('Something went wrong. Please try again.')
            }
          } else {
            this.openErrorModal('Network error. Please check your connection.')
          }
        })
        .finally(() => {
          this.sellRequestActionLoading = false
        })
    },
    login2(data) {
      this.error = ''
      this.$http
        .post(process.env.VUE_APP_API + 'login', data)
        .then((res) => {
          if (res.data.status == 'OK') {
            this.isAuth = true
          }
        })
        .catch(() => {})
    },
    closeSignInModal() {
      this.signInModalIsVisible = false
    },
    openSignInModal2() {
      this.signInModalIsVisible = true
    },
    setAmount(am) {
      this.amount = am
    },
    closeProductModal() {
      this.productModalIsVisible = false
    },
    openProductModal(product) {
      this.productModalIsVisible = true
      this.chosenProoduct = product
    },
    scrollToSection(section) {
      if (this.$route.name != 'Home') {
        this.$router.push({ path: '/' })
      }
      setTimeout(() => {
        const firstScrollTo = scroller()
        firstScrollTo(section)
      }, 200)
    },
    getDomainName(hostName) {
      return hostName.substring(
        hostName.lastIndexOf('.', hostName.lastIndexOf('.') - 1) + 1,
      )
    },
    closeMenu() {
      this.menuVisible = false
      document.body.style.overflow = 'auto'
    },
    toggleMenu() {
      this.menuVisible = !this.menuVisible
      window.scrollTo(0, 0)
      if (this.menuVisible) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'auto'
      }
    },
    menuHeight() {
      const doc = document.documentElement
      doc.style.setProperty('--vh', `${window.innerHeight * 0.01}px`)
    },
    openSignInModal() {
      window.location.replace('https://api.rustcores.com/auth/steam/redirect')
    },
    logout() {
      this.$http
        .get(process.env.VUE_APP_API + 'user/logout')
        .then(() => {
          this.isAuth = false
          this.$router.push({ path: '/' })
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignInModal()
          }
        })
    },
    getDepositHistory() {
      this.$http
        .get(process.env.VUE_APP_API + 'payments/history')
        .then((res) => {
          this.depositHistory = res.data.data
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignInModal()
          }
        })
    },
    getOrderHistory() {
      this.$http
        .get(process.env.VUE_APP_API + 'orders/history')
        .then((res) => {
          this.orderHistory = res.data.data
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignInModal()
          }
        })
    },
    formatDate(num) {
      let objectDate = new Date(num * 1000)
      let day = objectDate.getDate()
      if (day < 10) {
        day = '0' + day
      }
      let month = objectDate.getMonth() + 1
      if (month < 10) {
        month = `0${month}`
      }
      let year = objectDate.getFullYear()

      function padTo2Digits(x) {
        return String(x).padStart(2, '0')
      }
      let hoursAndMinutes =
        padTo2Digits(objectDate.getHours()) +
        ':' +
        padTo2Digits(objectDate.getMinutes())
      let format = day + '.' + month + '.' + year + ' ' + hoursAndMinutes

      return format
    },
    openProduct(item) {
      this.$router.push({
        path: '/products/' + item.id,
        params: { data: item.id },
      })
      this.getProductPageContent(item.id)
    },
    goToPage(item) {
      let newItem
      if (item == 'privacy') {
        for (let i = 0; i < this.textPageList.length; i++) {
          if (this.textPageList[i].title.toLowerCase().includes('privacy')) {
            newItem = this.textPageList[i].id
          }
        }
      } else if (item == 'terms') {
        for (let i = 0; i < this.textPageList.length; i++) {
          if (this.textPageList[i].title.toLowerCase().includes('terms')) {
            newItem = this.textPageList[i].id
          }
        }
      } else if (item == 'cookie') {
        for (let i = 0; i < this.textPageList.length; i++) {
          if (this.textPageList[i].title.toLowerCase().includes('cookie')) {
            newItem = this.textPageList[i].id
          }
        }
      } else {
        newItem = item
      }
      item.slug
        ? this.$router.push({ path: '/' + newItem, params: { data: newItem } })
        : this.$router.push({
            path: '/pages/' + newItem,
            params: { data: newItem },
          })
      this.getTextPageContent(newItem)
    },
    getTextPageContent(id) {
      this.textPageTitle = ''
      this.textPageHeader = ''
      this.textPageContent = ''
      this.$http
        .get(process.env.VUE_APP_API + 'static-pages/' + id)
        .then((res) => {
          this.textPageTitle = res.data.payload.title
          this.textPageContent = res.data.payload.content
          this.textPageHeader = res.data.payload.header
        })
        .catch(() => {})
    },
    getSettings() {
      return this.$http
        .get(process.env.VUE_APP_API + 'settings')
        .then(async (res) => {
          this.ip = res.data.payload.game_server.host
          this.port = res.data.payload.game_server.port
          this.settings = res.data.payload
          const snippet = res.data.payload.google_tag
          if (!this.analyticsInitialized && snippet) {
            await loadScriptsFromString(snippet)
            this.analyticsInitialized = true
          }
        })
        .catch(() => {})
    },
    getTextPages() {
      this.$http
        .get(process.env.VUE_APP_API + 'static-pages')
        .then((res) => {
          this.textPageList = res.data.payload
        })
        .catch((res) => {
          console.log(res)
        })
    },
    topUp(data) {
      this.$http
        .post(process.env.VUE_APP_API + 'payments/checkout', data)
        .then((res) => {
          if (res.data.status == 'OK') {
            window.location = res.data.redirect_url
          } else {
            this.error = res.response.data.message
          }
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignInModal()
          } else {
            this.error = res.response.data.message
          }
        })
    },
    getProfileData() {
      let self = this
      self.$http
        .get(process.env.VUE_APP_API + 'user/profile')
        .then((res) => {
          self.getOrderHistory()
          self.getDepositHistory()
          self.getSellRequestHistory()
          self.profileData = res.data.payload
        })
        .catch(() => {})
    },
    getCurrencies() {
      return this.$http
        .get(process.env.VUE_APP_API + 'currencies')
        .then((res) => {
          const that = this
          this.currencyOptions = res.data.payload

          if (localStorage.getItem('currency')) {
            this.currencyCode = localStorage.getItem('currency')

            const currency = res.data.payload.find(
              ({ code }) => code === that.currencyCode,
            )

            if (currency) {
              this.currencyValue = currency.value
              this.currency = currency.symbol
            } else {
              // Если валюта больше не существует, переключаемся на первую доступную
              this.currencyCode = this.currencyOptions[0].code
              this.currency = this.currencyOptions[0].symbol
              this.currencyValue = this.currencyOptions[0].value
              localStorage.setItem('currency', this.currencyCode)
            }
          } else {
            this.currencyCode = this.currencyOptions[0].code
            this.currency = this.currencyOptions[0].symbol
            this.currencyValue = this.currencyOptions[0].value
            localStorage.setItem('currency', this.currencyCode)
          }
        })
        .catch(() => {})
    },
    changeCurrency(item) {
      this.currencyCode = item
      this.checkCurrency()
    },
    checkCurrency() {
      const that = this
      const currency = this.currencyOptions.find(
        ({ code }) => code === that.currencyCode,
      )

      if (currency) {
        this.currency = currency.symbol
        this.currencyValue = currency.value
        console.log(currency.symbol)

        if (this.isAuth) {
          this.getProfileData()
        }
      }
    },
    async fetchLanguagesAndSetFirst() {
      try {
        const response = await this.$http.get(
          process.env.VUE_APP_API + 'languages',
        )
        if (
          response.data &&
          response.data.status === 'OK' &&
          response.data.payload.length
        ) {
          this.languages = response.data.payload
          this.selectedLanguageId = this.languages[0].id // Set the first language
          const responseLocal = await this.$http.get(
            process.env.VUE_APP_API + 'localizations',
            {
              params: { lang_id: this.selectedLanguageId },
            },
          )
          if (responseLocal.data) {
            const currentLangCode = this.currentLanguageCode
            if (currentLangCode) {
              i18n.setLocaleMessage(currentLangCode, responseLocal.data)
              i18n.locale = currentLangCode
            }
          }
        }
      } catch (e) {
        console.error('Failed to fetch languages or localizations:', e)
      }
    },
  },
  mounted: async function () {
    await this.fetchLanguagesAndSetFirst()
    if (localStorage.getItem('age')) {
      this.ageModalIsVisible = false
    }
    this.cookiesModal()
    this.hostname = this.getDomainName(window.location.hostname)
    this.currency = process.env.VUE_APP_CURRENCY
    this.imgDomain = process.env.VUE_APP_STATIC_DOMAIN
    this.pageLoaded = true
    let self = this
    window.addEventListener('resize', self.menuHeight)
    self.getSettings()
    self.menuHeight()
    self.getTextPages()
    self.getCurrencies()
    self.$http
      .get(process.env.VUE_APP_API + 'user/is-auth')
      .then((res) => {
        console.log('auth: ' + res.data.authenticated)
        self.isAuth = res.data.authenticated

        if (res.data.authenticated) {
          self.getProfileData()
        }
      })
      .catch(() => {})
    this.fetchLanguagesAndSetFirst()
  },
  computed: {
    minOrderAmount: function () {
      return this.settings && this.settings.min_order_amount
    },
    currentLanguageName() {
      const lang = this.languages.find((l) => l.id == this.selectedLanguageId)
      return lang ? lang.title : 'Loading...'
    },
    currentLanguageCode() {
      const lang = this.languages.find((l) => l.id == this.selectedLanguageId)
      return lang ? lang.code : ''
    },
  },
}
</script>
